/**
 * `/programs/:id` — the customer end program, which is the unit of value in a
 * design-win funnel. A program spans accounts, and the opportunities attached
 * to it may be owned by different reps.
 *
 * That is exactly why **"program cancelled → close every attached
 * opportunity" is visible only to a Manager or an Admin** (§2 rule 3): under
 * the ordinary program write rule an account owner could bulk-close
 * opportunities owned by other reps on other accounts, which is an escalation
 * route around `canWriteOpportunity`. The API refuses it for a `User`, and the
 * button is not offered.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useMe } from '@/auth/useMe';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/label';
import { NotesPanel } from '@/features/notes/NotesPanel';
import { MoneyCell } from '@/components/domain/Money';
import { formatDate, formatMoney, humanize, sumMoney, today } from '@/lib/format';

export function ProgramDetail(): React.ReactElement {
  const { id = '' } = useParams();
  const { data: me } = useMe();
  const [cancelling, setCancelling] = useState(false);

  const program = useApiQuery<{ program: Row }>(['program', id], `/api/programs/${id}`);
  const opportunities = useApiQuery<Paged<Row>>(
    ['opportunities', 'program', id],
    `/api/opportunities?programId=${id}&pageSize=200`,
  );

  const record = program.data?.program ?? {};
  const attached = opportunities.data?.items ?? [];
  const total = sumMoney(attached.map((row) => row['annual_revenue_net'] as string));
  const isManager = me?.tier === 'Manager' || me?.tier === 'Admin';

  return (
    <div className="space-y-4">
      <header className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{String(record['name'] ?? '')}</h1>
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{humanize(String(record['end_market']))}</Badge>
              <Badge variant="outline">{humanize(String(record['status']))}</Badge>
              {record['expected_sop_date'] !== null && (
                <span>SOP {formatDate(record['expected_sop_date'] as string)}</span>
              )}
              {record['expected_program_years'] !== null && (
                <span>{String(record['expected_program_years'])} year programme</span>
              )}
              {record['export_sensitive'] === true && (
                <Badge variant="risk">Export-sensitive</Badge>
              )}
            </p>
          </div>

          {isManager && record['status'] !== 'cancelled' && (
            <Button variant="destructive" size="sm" onClick={() => setCancelling(true)}>
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              Cancel the programme
            </Button>
          )}
        </div>

        {record['description'] !== null && record['description'] !== undefined && (
          <p className="mt-2 text-sm">{String(record['description'])}</p>
        )}
      </header>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-medium">Attached opportunities</h2>
          <p className="text-sm">
            {attached.length} · {formatMoney(total)} annual, Menlo net
          </p>
        </div>
        <ul className="divide-y">
          {attached.map((row) => (
            <li
              key={String(row['id'])}
              className="flex items-center justify-between gap-2 py-1.5 text-sm"
            >
              <span className="min-w-0">
                <Link
                  to={`/opportunities/${String(row['id'])}`}
                  className="block truncate font-medium hover:underline"
                >
                  {String(row['name'])}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {String(row['account_name'])} · {String(row['stage_name'])} ·{' '}
                  {String(row['owner_name'] ?? 'unowned')}
                </span>
              </span>
              <MoneyCell value={row['annual_revenue_net'] as string} />
            </li>
          ))}
          {attached.length === 0 && (
            <li className="py-3 text-xs text-muted-foreground">Nothing attached yet.</li>
          )}
        </ul>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <NotesPanel entityType="program" entityId={id} canWrite compact />
      </section>

      {cancelling && (
        <CancelProgramDialog
          programId={id}
          count={attached.length}
          onClose={() => setCancelling(false)}
          onDone={() => {
            setCancelling(false);
            void program.refetch();
            void opportunities.refetch();
          }}
        />
      )}
    </div>
  );
}

function CancelProgramDialog({
  programId,
  count,
  onClose,
  onDone,
}: {
  programId: string;
  count: number;
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const [closeDate, setCloseDate] = useState(today());

  const cancel = useApiMutation<{ closed_open: number; lapsed_won: number }, void>(
    () => ({
      path: `/api/programs/${programId}/cancel`,
      method: 'POST',
      body: { close_date: closeDate, note: 'Programme cancelled' },
    }),
    { invalidate: [['program', programId]], onSuccess: onDone },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Cancel the programme"
      description={`This closes every opportunity attached to it — ${count} on this page, across accounts and owners.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep it open
          </Button>
          <Button variant="destructive" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            Cancel the programme
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Close date">
          <Input
            type="date"
            value={closeDate}
            onChange={(event) => setCloseDate(event.target.value)}
          />
        </Field>
        <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
          <li>Open opportunities are closed as no decision.</li>
          <li>
            Won opportunities not yet in production are closed as <strong>design win lapsed</strong>{' '}
            — the win is kept, and a new closure records that it expired without becoming production
            revenue.
          </li>
          <li>
            This writes opportunities you may not own individually, which is why it is restricted to
            managers and administrators. It is audited.
          </li>
        </ul>
      </div>
    </Dialog>
  );
}
